import {
  ArrowRight,
  Check,
  ChevronDown,
  ShieldCheck,
  Code2,
  Cloud,
  Sparkles,
  Database,
  Braces,
  GitPullRequest,
  Laptop,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import "./NativePlatform.css";
import PillarVisual from "./PillarVisuals";
import SecurityLandscape from "./SecurityLandscape";
import EndpointLanding from "./EndpointLanding";

const icons = [Code2, Cloud, Sparkles, Database, Braces, Laptop];
const shortNames = ["Applications", "Cloud", "AI", "Data", "APIs", "Endpoints"];

export function NativeHeroVisual() {
  return (
    <div className="native-hero-visual">
      <div className="hero-domain-rail" aria-label="Security areas">
        {shortNames.map((name, index) => {
          const Icon = icons[index];
          return (
            <a key={name} href={`#${productAreas[index].id}`}>
              <Icon size={17} strokeWidth={1.7} />
              {name}
            </a>
          );
        })}
        <span className="domain-rail-note">Native scanners. One platform.</span>
      </div>
      <SecurityLandscape />
      <div className="hero-stage-footer">
        <span>
          <ShieldCheck size={14} />
          Built-in detection
        </span>
        <span>
          <Sparkles size={14} />
          AI-assisted investigation
        </span>
        <span>
          <GitPullRequest size={14} />
          Reviewable fixes
        </span>
      </div>
    </div>
  );
}

const productAreas = [
  {
    id: "application-security",
    name: "Application Security",
    category: "ASPM",
    description:
      "Bring native scanning, application posture, and AI-assisted triage into one place. Review coverage, investigate prioritized findings, and move supported fixes into your development workflow.",
    benefits: [
      "See application risks, scan coverage, and priority actions.",
      "Review AI insights and prepare supported fixes or tickets.",
    ],
    technical:
      "Application Security Posture Management (ASPM) with built-in code, dependency, secrets, container, infrastructure, dynamic application, and mobile scanning. Includes SAST, SCA, DAST, SBOM, CryptoBOM, correlation, AI-assisted triage, and configurable dependency/container update proposals. Repository exposure tracking and eligible auto-revert are also available.",
  },
  {
    id: "cloud-security",
    name: "Cloud Security",
    category: "CSPM",
    description:
      "Review the posture of your cloud assets, investigate misconfigurations, and explore connected attack paths. Bring IAM, Kubernetes, and compliance context into your investigation.",
    benefits: [
      "Understand asset exposure and the findings behind it.",
      "Review identity permissions, attack paths, and compliance posture.",
    ],
    technical:
      "Cloud Security Posture Management (CSPM): cloud asset inventory, misconfigurations, IAM analysis, Kubernetes security, compliance posture, and attack path analysis. Coverage depends on your connected accounts and available scan data.",
  },
  {
    id: "ai-security",
    name: "AI Security",
    category: "AI-SPM",
    description:
      "Track AI security posture and changes across discovered components. Explore AI inventory, agents, and MCP, with discovery coverage and an AI bill of materials to support your review.",
    benefits: [
      "Review AI posture, changes, and discovery coverage.",
      "Investigate AI components, agents, MCP, and AI-BOM.",
    ],
    technical:
      "AI Security Posture Management (AI-SPM), AI inventory, risk overview, discovery coverage, agents and MCP visibility, and AI-BOM. Inventory and posture reflect the discovery data available to HTSOne.",
  },
  {
    id: "data-security",
    name: "Data Security",
    category: "DSPM",
    description:
      "Discover data stores and review how sensitive data is classified. Connect classification evidence with public access, encryption, and scan coverage to see where investigation is needed.",
    benefits: [
      "Review sensitive, public, and unencrypted data-store findings.",
      "Inspect classification evidence, data flows, and framework mappings.",
    ],
    technical:
      "Data Security Posture Management (DSPM) includes cloud data-store inventory, sensitivity classification, public/encryption posture, schema evidence and scan coverage. Repository data classification adds sensitive data locations, flows, risks, and framework mappings. Evidence and supported scanning depth vary by data source and permissions.",
  },
  {
    id: "api-security",
    name: "API Security",
    category: "API SECURITY",
    description:
      "Discover APIs from observed traffic and investigate endpoint risk with request, response, and sensitive-data context. Review inventory, runtime relationships, and security findings in a dedicated workspace.",
    benefits: [
      "Explore endpoint inventory, traffic, and runtime relationships.",
      "Prioritize endpoint findings with exposure and data context.",
    ],
    technical:
      "API inventory/catalog, traffic and events, runtime map, risky endpoints, misconfigurations, and security assessment findings. Runtime visibility reflects observed traffic in the selected time window.",
  },
  {
    // Rendered by EndpointLanding, which owns this area's copy, console
    // preview, and coverage questions.
    id: "endpoint-security",
    name: "Endpoint Security",
    category: "ENDPOINT SECURITY",
  },
];

const featureTitles = [
  "Make the next change a secure one.",
  "Get to the evidence behind the exposure.",
  "Bring your AI estate into view.",
  "Connect sensitive data to its exposure.",
  "See the risk in a real request.",
];
const featureEyebrows = [
  "FIND IT. UNDERSTAND IT. FIX IT.",
  "EXPOSURE WITH CONTEXT",
  "VISIBILITY BEYOND THE MODEL",
  "SENSITIVITY MEETS EXPOSURE",
  "FROM TRAFFIC TO FINDINGS",
];
const featureCaptions = [
  "From application assets to a reviewable next step.",
  "The finding. The exposure. The evidence.",
  "See where an AI component was discovered.",
  "Follow the questions that matter.",
  "Understand what an endpoint puts at risk.",
];

export default function NativePlatform() {
  const [active, setActive] = useState(() => {
    const index = productAreas.findIndex(
      (area) => `#${area.id}` === window.location.hash,
    );
    return index < 0 ? 1 : index;
  });
  const tabRefs = useRef([]);
  const featureSection = useRef(null);
  useEffect(() => {
    const followHash = () => {
      const index = productAreas.findIndex(
        (area) => `#${area.id}` === window.location.hash,
      );
      if (index >= 0) {
        setActive(index);
        featureSection.current?.scrollIntoView({ block: "start" });
      }
    };
    followHash();
    window.addEventListener("hashchange", followHash);
    return () => window.removeEventListener("hashchange", followHash);
  }, []);
  const select = (index) => {
    setActive(index);
    window.history.replaceState(
      window.history.state,
      "",
      `#${productAreas[index].id}`,
    );
  };
  const onTabKey = (event, index) => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % productAreas.length;
    else if (event.key === "ArrowLeft")
      next = (index + productAreas.length - 1) % productAreas.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = productAreas.length - 1;
    else return;
    event.preventDefault();
    select(next);
    tabRefs.current[next]?.focus();
  };
  return (
    <section
      ref={featureSection}
      id="features"
      className="native-platform"
      aria-labelledby="platform-heading"
    >
      <div className="platform-heading-row">
        <div>
          <span className="native-eyebrow">
            <span /> THE HTSONE PLATFORM
          </span>
          <h2 id="platform-heading">
            A wider view.
            <br />
            <span>A clearer next step.</span>
          </h2>
        </div>
        <p>
          Six security areas. Our own scanning engines.
          <br />
          Explore the product, one meaningful detail at a time.
        </p>
      </div>
      <div className="platform-tabs" role="tablist" aria-label="Security areas">
        {productAreas.map((area, index) => {
          const Icon = icons[index];
          return (
            <button
              key={area.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`tab-${area.id}`}
              aria-selected={active === index}
              aria-controls={area.id}
              tabIndex={active === index ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={(event) => onTabKey(event, index)}
            >
              <Icon size={19} strokeWidth={1.7} />
              <span>{area.name}</span>
            </button>
          );
        })}
      </div>
      {productAreas.map((area, index) => (
        <div
          key={area.id}
          id={area.id}
          role="tabpanel"
          aria-labelledby={`tab-${area.id}`}
          hidden={active !== index}
          tabIndex={0}
          className="platform-panel"
        >
          {area.id === "endpoint-security" ? (
            <EndpointLanding />
          ) : (
            <>
              <div className="platform-story">
                <div>
                  <span className="story-eyebrow">{featureEyebrows[index]}</span>
                  <h3>{featureTitles[index]}</h3>
                  <p>{area.description}</p>
                </div>
                <ul>
                  {area.benefits.map((benefit) => (
                    <li key={benefit}>
                      <Check size={15} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`platform-proof proof-${index}`}>
                <div className="proof-heading">
                  <span>{featureCaptions[index]}</span>
                </div>
                <PillarVisual area={index} active={active === index} />
                <div className="proof-bottom">
                  <span>ILLUSTRATED CAPABILITY</span>
                  <span>
                    {index === 3
                      ? "Discover â†’ Classify â†’ Investigate"
                      : "Detection with the context to act"}
                  </span>
                </div>
              </div>
              <details className="platform-technical">
                <summary>
                  Explore {area.category} coverage <ChevronDown size={16} />
                </summary>
                <p>{area.technical}</p>
              </details>
            </>
          )}
        </div>
      ))}
      <div className="platform-principle">
        <ShieldCheck size={21} />
        <p>
          <strong>Built to find risk. Designed to help you act.</strong>
          <span>
            Native scanning, evidence, and remediation in the same platform.
          </span>
        </p>
        <a href="#integrations">
          Fits your workflow <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}
